import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, RefreshControl, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Divider, Scroll, Stack } from '@/components/layout';
import { Loading, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import CoGuardianProfileContent from '@/features/guardian-player/components/co-guardian-profile';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import {
  GuardianCircleMemberStatus,
  type GuardianCircleMember,
} from '@/features/guardian-player/types';
import { formatGuardianApiDate } from '@/features/guardian-player/utils';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

import GuardianCircleEmptyState from './guardian-circle-empty-state';
import GuardianCircleMemberRow from './guardian-circle-member-row';
import InviteAdultsButton from './invite-adults-button';
import LeaveCircleButton from './leave-circle-button';

const strings = STRINGS.guardianPlayer.guardianCircle;

export type GuardianCircleContentProps = {
  /** Player whose circle this is — needed to drive the tablet co-guardian profile pane. */
  player: GuardianPlayer;
  firstName: string;
  members: GuardianCircleMember[];
  activeAdultsCount: number;
  isPrimaryGuardian: boolean;
  onBack: () => void;
  onPressMember: (member: GuardianCircleMember) => void;
  onInvite: () => void;
  onLeave: () => void;
  /** Remove-from-circle for the co-guardian shown in the tablet right pane. */
  onRemoveMember: (member: GuardianCircleMember) => void;
  isLoading?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
};

export default function GuardianCircleContent({
  player,
  firstName,
  members,
  activeAdultsCount,
  isPrimaryGuardian,
  onBack,
  onPressMember,
  onInvite,
  onLeave,
  onRemoveMember,
  isLoading = false,
  onRefresh,
  refreshing = false,
}: GuardianCircleContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();

  // Pull-to-refresh — native only (RN's RefreshControl is a no-op on web).
  const refreshControl =
    Platform.OS !== 'web' && onRefresh ? (
      <RefreshControl
        refreshing={refreshing}
        onRefresh={onRefresh}
        tintColor={guardianColors['Main/Main']}
      />
    ) : undefined;

  const screenTitle = strings.screenTitle.replace('{name}', firstName);
  const hasOtherGuardians = members.some(
    member =>
      !member.isSelf &&
      (member.status === GuardianCircleMemberStatus.Joined ||
        member.status === GuardianCircleMemberStatus.Creator),
  );

  const subtitle = (hasOtherGuardians ? strings.subtitle : strings.subtitleSolo)
    .replace('{count}', String(activeAdultsCount))
    .replace('{name}', firstName);
  const leaveLabel = strings.leave.replace('{name}', firstName);

  // Primary guardian sees the player's profile-created date on each member row's meta line.
  const playerCreatedLabel = useMemo(() => {
    const date = formatGuardianApiDate(player.createdOn, 'MMM yyyy');
    return date ? strings.metaCreated.replace('{date}', date) : undefined;
  }, [player.createdOn]);

  // Tablet/web is a master-detail view: tapping a joined co-guardian swaps the right
  // pane; invitations (and phone taps) push the relevant detail screen via the parent.
  const [selectedMemberId, setSelectedMemberId] = useState<string>();

  const joinedMembers = useMemo(
    () => members.filter(member => member.status === 'joined' || member.status === 'creator'),
    [members],
  );

  useEffect(() => {
    if (!isTablet || joinedMembers.length === 0) {
      setSelectedMemberId(undefined);
      return;
    }
    const stillExists = joinedMembers.some(member => member.id === selectedMemberId);
    if (!stillExists) {
      const firstOtherMember = joinedMembers.find(member => !member.isSelf);
      setSelectedMemberId(firstOtherMember ? firstOtherMember.id : undefined);
    }
  }, [isTablet, joinedMembers, selectedMemberId]);

  const selectedMember = useMemo(
    () => members.find(member => member.id === selectedMemberId),
    [members, selectedMemberId],
  );

  const handleRowPress = useCallback(
    (member: GuardianCircleMember) => {
      if (isTablet && (member.status === 'joined' || member.status === 'creator')) {
        setSelectedMemberId(member.id);
        return;
      }
      onPressMember(member);
    },
    [isTablet, onPressMember],
  );

  const scrollContentStyle = useMemo(
    () => [styles.content, { paddingBottom: scale(24) }],
    [scale, styles.content],
  );

  const inviteNode = isPrimaryGuardian ? (
    <InviteAdultsButton label={strings.invite} onPress={onInvite} />
  ) : null;

  const leaveNode = isPrimaryGuardian ? null : (
    <LeaveCircleButton label={leaveLabel} onPress={onLeave} />
  );

  // Tablet places the co-guardian's Leave action in the header (design); phone keeps it
  // at the bottom of the screen. Primary guardians never see it.
  const leaveHeaderIcon = isPrimaryGuardian ? undefined : ('IcLeave' as const);

  const subtitleNode = (
    <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.subtitle}>
      {subtitle}
    </Text>
  );

  if (isLoading) {
    return (
      <Stack style={styles.tabletRoot}>
        <GuardianScreenHeader
          title={screenTitle}
          onBack={onBack}
          titleAlign="center"
          containerProps={isTablet ? undefined : { backgroundColor: 'transparent' }}
        />
        <Stack flex={1} align="center" justify="center">
          <Loading />
        </Stack>
      </Stack>
    );
  }

  if (isTablet) {
    return (
      <Stack style={styles.tabletRoot}>
        <GuardianScreenHeader
          title={screenTitle}
          onBack={onBack}
          rightIcon={leaveHeaderIcon}
          rightLabel={leaveLabel}
          rightColor={colors['Level 3']}
          rightAccessibilityLabel={leaveLabel}
          onPressRight={onLeave}
        />
        <Stack direction="row" flex={1} style={styles.tabletBody}>
          <Stack style={styles.sidebar}>
            <Scroll
              style={styles.scrollFill}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.sidebarContent}
              refreshControl={refreshControl}>
              {subtitleNode}
              <Stack style={styles.memberList}>
                {members.map(member => (
                  <GuardianCircleMemberRow
                    key={member.id}
                    member={member}
                    onPress={handleRowPress}
                    selected={member.id === selectedMemberId}
                    isPrimaryGuardian={isPrimaryGuardian}
                    playerCreatedLabel={playerCreatedLabel}
                  />
                ))}
              </Stack>
              {inviteNode}
            </Scroll>
          </Stack>

          <View style={styles.tabletDivider} />

          <Stack flex={1} style={styles.detailPane}>
            {hasOtherGuardians ? (
              <CoGuardianProfileContent
                key={selectedMember?.id}
                player={player}
                guardianUid={selectedMember?.guardianUid}
                isPrimaryGuardian={isPrimaryGuardian}
                onRemove={() => {
                  if (selectedMember) onRemoveMember(selectedMember);
                }}
              />
            ) : (
              <GuardianCircleEmptyState
                firstName={firstName}
                isPrimaryGuardian={isPrimaryGuardian}
                onInvite={onInvite}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      viewType="gradient"
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.root}>
      <GuardianScreenHeader
        title={screenTitle}
        onBack={onBack}
        titleAlign="center"
        containerProps={{ backgroundColor: 'transparent' }}
      />

      <Scroll
        style={styles.scrollFill}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={scrollContentStyle}
        refreshControl={refreshControl}>
        {subtitleNode}

        <Stack style={styles.card}>
          {members.map((member, index) => (
            <Fragment key={member.id}>
              {index > 0 ? <Divider color={guardianColors['3']} /> : null}
              <GuardianCircleMemberRow
                member={member}
                onPress={handleRowPress}
                isPrimaryGuardian={isPrimaryGuardian}
                playerCreatedLabel={playerCreatedLabel}
              />
            </Fragment>
          ))}
        </Stack>

        {inviteNode}
      </Scroll>

      {leaveNode ? (
        <Stack paddingX={scale(16)} style={{ paddingBottom: insets.bottom + scale(16) }}>
          {leaveNode}
        </Stack>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    flex: 1,
  },
  scrollFill: {
    flex: 1,
  },
  content: {
    paddingHorizontal: scale(16),
    gap: scale(20),
  },
  subtitle: {
    color: guardianColors['6'],
    // backgroundColor: 'red',
    // Android adds ascent/descent font padding on top of lineHeight, inflating the box
    // height (iOS doesn't). Turn it off so the text box hugs the glyphs.
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  memberList: {
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    padding: scale(12),
  },
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingHorizontal: scale(12),
    paddingVertical: scale(4),
  },
  tabletRoot: {
    flex: 1,
    backgroundColor: guardianColors['1'],
  },
  tabletBody: {
    borderTopWidth: 1,
    borderTopColor: guardianColors['3'],
  },
  sidebar: {
    width: '37%',
  },
  sidebarContent: {
    padding: scale(16),
    gap: scale(16),
  },
  tabletDivider: {
    width: 1,
    backgroundColor: guardianColors['3'],
    marginVertical: scale(16),
  },
  detailPane: {
    // detail content wrapper
  },
}));
